/**
 * Cart drawer
 *
 * Owns the whole cart interaction on non-cart pages when `settings.cart_use_drawer` is on. The
 * legacy mini-cart code in theme.js bails out in that case (see the `useCartDrawer` guard in the
 * Cart component), so nothing here has to co-exist with it.
 *
 * Like the mini-cart it re-renders from Liquid rather than patching the DOM by hand, which keeps
 * app integrations and cart transformations working.
 */
class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.isOpen = false;

    this.addEventListener('click', this._onClick.bind(this));
    this.addEventListener('change', this._onChange.bind(this));
    this.addEventListener('keydown', this._onKeyDown.bind(this));

    this._onDocumentKeyUp = this._onDocumentKeyUp.bind(this);
    this._onDocumentClick = this._onDocumentClick.bind(this);
    this._onProductAdded = this._onProductAdded.bind(this);
    this._onCartRefresh = this._onCartRefresh.bind(this);

    this._onWindowResize = () => this._syncCrossSellBar();

    document.addEventListener('keyup', this._onDocumentKeyUp);
    document.documentElement.addEventListener('click', this._onDocumentClick);
    document.documentElement.addEventListener('product:added', this._onProductAdded);
    document.documentElement.addEventListener('cart:refresh', this._onCartRefresh);
    window.addEventListener('resize', this._onWindowResize);
  }

  disconnectedCallback() {
    document.removeEventListener('keyup', this._onDocumentKeyUp);
    document.documentElement.removeEventListener('click', this._onDocumentClick);
    document.documentElement.removeEventListener('product:added', this._onProductAdded);
    document.documentElement.removeEventListener('cart:refresh', this._onCartRefresh);
    window.removeEventListener('resize', this._onWindowResize);
  }

  _onDocumentClick(event) {
    const toggle = event.target.closest('[data-action="open-cart-drawer"]');

    if (toggle) {
      event.preventDefault();
      this.open();
    }
  }

  open() {
    if (this.isOpen) {
      return;
    }

    this.isOpen = true;
    this.setAttribute('aria-hidden', 'false');
    this.classList.add('is-open');
    document.body.classList.add('cart-drawer-open');

    this.previouslyFocusedElement = document.activeElement;

    // Deferred so the panel is visible (and therefore focusable) before we move focus into it
    requestAnimationFrame(() => {
      const focusTarget = this.querySelector('.cart-drawer__close');

      if (focusTarget) {
        focusTarget.focus();
      }
    });

    this._loadRecommendations();
    this._initCrossSellScroller();
  }

  close() {
    if (!this.isOpen) {
      return;
    }

    this.isOpen = false;
    this.setAttribute('aria-hidden', 'true');
    this.classList.remove('is-open');
    document.body.classList.remove('cart-drawer-open');

    if (this.previouslyFocusedElement && document.body.contains(this.previouslyFocusedElement)) {
      this.previouslyFocusedElement.focus();
    }
  }

  _onClick(event) {
    const target = event.target.closest('[data-cart-drawer-action]');

    if (!target) {
      return;
    }

    switch (target.getAttribute('data-cart-drawer-action')) {
      case 'close':
        event.preventDefault();
        this.close();
        break;

      case 'change-quantity':
        event.preventDefault();
        this._changeQuantity(target.getAttribute('data-line'), parseInt(target.getAttribute('data-quantity'), 10));
        break;

      case 'reveal-options':
        event.preventDefault();
        this._revealOptions(target);
        break;

      case 'cross-sell-prev':
        event.preventDefault();
        this._scrollCrossSell(-1);
        break;

      case 'cross-sell-next':
        event.preventDefault();
        this._scrollCrossSell(1);
        break;

      case 'add-item':
        event.preventDefault();
        this._addItem(target);
        break;
    }
  }

  // Cross-sell with variants: first click reveals the dropdown and turns the
  // button into "Add"; a second click adds the chosen variant.
  _revealOptions(button) {
    const card = button.closest('[data-cross-sell-card]');
    const select = card ? card.querySelector('[data-cross-sell-variant-select]') : null;

    if (select) {
      select.hidden = false;
      button.setAttribute('data-variant-id', select.value);
    }

    button.setAttribute('data-cart-drawer-action', 'add-item');
    button.textContent = button.getAttribute('data-add-label') || 'Add';
  }

  _onChange(event) {
    const variantSelect = event.target.closest('[data-cross-sell-variant-select]');

    if (variantSelect) {
      this._updateCrossSellCard(variantSelect);
      return;
    }

    const input = event.target.closest('[data-cart-drawer-quantity-input]');

    if (!input) {
      return;
    }

    let quantity = parseInt(input.value, 10);

    if (isNaN(quantity) || quantity < 0) {
      // Put back the value the server knows about
      this._render();
      return;
    }

    // The line carries what the storefront will sell, which is below the shelf
    // count on tracked variants (the stock floor — see docs/stock-floor.md).
    // A typed number bypasses the +/- buttons, so clamp it here too.
    const max = parseInt(input.getAttribute('data-max-quantity'), 10);

    if (!isNaN(max) && quantity > max) {
      quantity = max;
    }

    this._changeQuantity(input.getAttribute('data-line'), quantity);
  }

  _onKeyDown(event) {
    if (event.key === 'Enter' && event.target.closest('[data-cart-drawer-quantity-input]')) {
      // Otherwise the enter key submits the form and sends the customer straight to checkout
      event.preventDefault();
      event.target.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (event.key === 'Tab' && this.isOpen) {
      this._trapFocus(event);
    }
  }

  _onDocumentKeyUp(event) {
    if (event.key === 'Escape' && this.isOpen) {
      this.close();
    }
  }

  _onProductAdded() {
    this._render().then(() => this.open());
  }

  _onCartRefresh() {
    this._render();
  }

  _trapFocus(event) {
    const focusable = Array.from(
      this.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')
    ).filter((element) => element.offsetParent !== null);

    if (focusable.length === 0) {
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  _changeQuantity(line, quantity) {
    // Loading state on just the line item being changed (Dawn-style).
    const lineItem = this.querySelector(`[data-line-item="${line}"]`);

    if (lineItem) {
      lineItem.classList.add('is-loading');
    }

    return fetch(`${window.routes.cartChangeUrl}.js`, {
      body: JSON.stringify({ line: line, quantity: quantity }),
      credentials: 'same-origin',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then(() => this._render())
      .catch(() => {
        if (lineItem) {
          lineItem.classList.remove('is-loading');
        }
      });
  }

  _addItem(button) {
    const card = button.closest('[data-cross-sell-card]');

    button.setAttribute('disabled', 'disabled');

    if (card) {
      card.classList.add('is-adding');
    }

    return fetch(`${window.routes.cartAddUrl}.js`, {
      body: JSON.stringify({ items: [{ id: parseInt(button.getAttribute('data-variant-id'), 10), quantity: 1 }] }),
      credentials: 'same-origin',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then(() => {
        // Brief "added" confirmation over the card, then fade the new item into the cart.
        if (card) {
          card.classList.remove('is-adding');
          card.classList.add('is-added');
        }

        return new Promise((resolve) => setTimeout(resolve, 550));
      })
      .then(() => this._render())
      .then(() => {
        const body = this.querySelector('.cart-drawer__body');

        if (body) {
          body.classList.add('is-refreshed');
          setTimeout(() => body.classList.remove('is-refreshed'), 500);
        }
      })
      .catch(() => {
        if (card) {
          card.classList.remove('is-adding');
        }

        button.removeAttribute('disabled');
      });
  }

  _setBusy(isBusy) {
    this.classList.toggle('is-busy', isBusy);
  }

  /**
   * Re-render the drawer from Liquid, preserving the scroll position of the item list.
   */
  _render() {
    return fetch(`${window.routes.cartUrl}?section_id=cart-drawer`, {
      credentials: 'same-origin',
      method: 'GET',
      headers: { 'Cache-Control': 'no-cache' }
    })
      .then((response) => response.text())
      .then((html) => {
        const holder = document.createElement('div');
        holder.innerHTML = html;

        const newDrawer = holder.querySelector('cart-drawer');

        if (!newDrawer) {
          return;
        }

        const currentList = this.querySelector('.cart-drawer__line-items');
        const scrollPosition = currentList ? currentList.scrollTop : null;

        this.innerHTML = newDrawer.innerHTML;
        this.setAttribute('data-item-count', newDrawer.getAttribute('data-item-count'));

        const newList = this.querySelector('.cart-drawer__line-items');

        if (newList && scrollPosition !== null) {
          newList.scrollTop = scrollPosition;
        }

        this._updateHeaderCount(newDrawer.getAttribute('data-item-count'));

        document.documentElement.dispatchEvent(new CustomEvent('cart:rerendered', { bubbles: true }));

        this._loadRecommendations();
        this._initCrossSellScroller();
      });
  }

  // Replace the collection fallback with Search & Discovery recommendations
  // (complementary, then related) for the highest-value item in the cart.
  _loadRecommendations() {
    const slot = this.querySelector('[data-cart-drawer-cross-sell]');

    if (!slot) {
      return;
    }

    const anchor = slot.getAttribute('data-anchor-id');

    if (!anchor) {
      return;
    }

    // Dedupe: only fetch once per anchor per rendered slot.
    if (slot.getAttribute('data-recs-state') === anchor) {
      return;
    }

    slot.setAttribute('data-recs-state', anchor);

    const limit = slot.getAttribute('data-limit') || '4';
    const root = (window.routes && window.routes.rootUrlWithoutSlash) || '';
    const base = `${root}/recommendations/products?section_id=cart-drawer-recommendations&product_id=${anchor}&limit=${limit}`;

    const tryIntent = (intent) =>
      fetch(`${base}&intent=${intent}`, { credentials: 'same-origin' })
        .then((response) => (response.ok ? response.text() : ''))
        .then((html) => {
          if (!html) {
            return null;
          }

          const holder = document.createElement('div');
          holder.innerHTML = html;

          const rendered = holder.querySelector('[data-cart-drawer-cross-sell-rendered]');

          return rendered && rendered.querySelector('.cart-drawer__cross-sell-item') ? rendered : null;
        })
        .catch(() => null);

    tryIntent('complementary')
      .then((node) => node || tryIntent('related'))
      .then((node) => {
        if (!node) {
          return;
        }

        const currentSlot = this.querySelector('[data-cart-drawer-cross-sell]');

        if (currentSlot && currentSlot.getAttribute('data-anchor-id') === anchor) {
          currentSlot.innerHTML = node.innerHTML;
          this._initCrossSellScroller();
        }
      });
  }

  // Cross-sell cards with variants: swap image, price and the Add target when a
  // variant is chosen from the dropdown.
  _updateCrossSellCard(select) {
    const card = select.closest('[data-cross-sell-card]');

    if (!card) {
      return;
    }

    const option = select.options[select.selectedIndex];

    if (!option) {
      return;
    }

    const button = card.querySelector('[data-cart-drawer-action="add-item"]');

    if (button) {
      button.setAttribute('data-variant-id', option.value);
    }

    const priceEl = card.querySelector('[data-cross-sell-price]');

    if (priceEl && option.dataset.price) {
      priceEl.textContent = option.dataset.price;
    }

    const compareEl = card.querySelector('[data-cross-sell-compare]');

    if (compareEl) {
      if (option.dataset.compare) {
        compareEl.textContent = option.dataset.compare;
        compareEl.hidden = false;
        if (priceEl) priceEl.classList.add('price--highlight');
      } else {
        compareEl.textContent = '';
        compareEl.hidden = true;
        if (priceEl) priceEl.classList.remove('price--highlight');
      }
    }

    const img = card.querySelector('.cart-drawer__cross-sell-image img');

    if (img && option.dataset.image) {
      img.src = option.dataset.image;
      img.removeAttribute('srcset');
    }
  }

  // Cross-sell scroller: prev/next arrows + custom pill scrollbar sync.
  _scrollCrossSell(direction) {
    const list = this.querySelector('.cart-drawer__cross-sell-list');

    if (!list) {
      return;
    }

    const card = list.querySelector('.cart-drawer__cross-sell-item');
    const amount = card ? card.getBoundingClientRect().width + 12 : list.clientWidth * 0.8;

    list.scrollBy({ left: direction * amount, behavior: 'smooth' });
  }

  _initCrossSellScroller() {
    const list = this.querySelector('.cart-drawer__cross-sell-list');

    if (list && !list.dataset.scrollerBound) {
      list.dataset.scrollerBound = 'true';
      list.addEventListener('scroll', () => this._syncCrossSellBar(), { passive: true });
    }

    const bar = this.querySelector('.cart-drawer__cross-sell-bar');

    if (list && bar && !bar.dataset.scrollerBound) {
      bar.dataset.scrollerBound = 'true';
      this._bindCrossSellBarDrag(list, bar);
    }

    this._syncCrossSellBar();
  }

  // Click anywhere on the pill bar to jump, or drag the thumb to scrub.
  _bindCrossSellBarDrag(list, bar) {
    const thumb = bar.querySelector('.cart-drawer__cross-sell-bar-thumb');
    let dragging = false;

    const scrollToPointer = (event) => {
      const barRect = bar.getBoundingClientRect();
      const thumbWidth = thumb ? thumb.getBoundingClientRect().width : 0;
      const travel = barRect.width - thumbWidth;

      if (travel <= 0) {
        return;
      }

      const offset = event.clientX - barRect.left - thumbWidth / 2;
      const ratio = Math.min(Math.max(offset / travel, 0), 1);

      list.scrollLeft = ratio * (list.scrollWidth - list.clientWidth);
    };

    bar.addEventListener('pointerdown', (event) => {
      dragging = true;
      bar.setPointerCapture(event.pointerId);
      bar.classList.add('is-dragging');
      scrollToPointer(event);
      event.preventDefault();
    });

    bar.addEventListener('pointermove', (event) => {
      if (dragging) {
        scrollToPointer(event);
      }
    });

    const endDrag = (event) => {
      if (!dragging) {
        return;
      }

      dragging = false;
      bar.classList.remove('is-dragging');

      if (bar.hasPointerCapture(event.pointerId)) {
        bar.releasePointerCapture(event.pointerId);
      }
    };

    bar.addEventListener('pointerup', endDrag);
    bar.addEventListener('pointercancel', endDrag);
  }

  _syncCrossSellBar() {
    const list = this.querySelector('.cart-drawer__cross-sell-list');
    const bar = this.querySelector('.cart-drawer__cross-sell-bar');
    const thumb = bar ? bar.querySelector('.cart-drawer__cross-sell-bar-thumb') : null;

    if (!list || !bar || !thumb) {
      return;
    }

    const ratio = list.clientWidth / list.scrollWidth;

    if (ratio >= 0.999) {
      bar.style.display = 'none';
      return;
    }

    bar.style.display = '';

    const thumbPct = Math.max(ratio * 100, 12);
    const maxScroll = list.scrollWidth - list.clientWidth;
    const posPct = maxScroll > 0 ? (list.scrollLeft / maxScroll) * (100 - thumbPct) : 0;

    thumb.style.width = thumbPct + '%';
    thumb.style.left = posPct + '%';
  }

  _updateHeaderCount(itemCount) {
    document.querySelectorAll('.header__cart-count').forEach((element) => {
      element.textContent = itemCount;
    });
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}
